import Vue from "vue";
import axios from "../plugins/axios";
import { getField, updateField } from "vuex-map-fields";
import { buildSubmissionPayload, expandSubmissionFormToBatchForms } from "@/utils/submissionPayloadBuilder";
import { readableReason } from "@/helpers/humanText";

const submissionRowId = (row) => row?.submissionId || row?.entity_id || row?.id || row?.platformQuoteId || null;

const quoteRowId = (q) => q?.platformQuoteId || q?.entity_id || q?.id || null;

const messageRowId = (m) => m?.entity_id || m?.id || m?.messageId || null;

const carrierKeyOf = (m) => m?.carrier_organization_id || m?.carrierOrganizationId || "";

const PRICING_SAVE_FAILED = "The pricing could not be saved. Please try again.";

/** One plain sentence for a refused pricing save; a refused field keeps the API's own. */
const pricingFailure = (e) => {
  const status = e?.response?.status;
  const data = e?.response?.data || {};
  if (status === 400) {
    return {
      success: false,
      message: readableReason(data.message, PRICING_SAVE_FAILED),
      errors: data.errors && typeof data.errors === "object" ? data.errors : {},
    };
  }
  if (status === 404) {
    return {
      success: false,
      message: "This quote is no longer on the submission. Close Pricing and reload the page.",
      errors: {},
    };
  }
  if (status === 403) {
    return { success: false, message: "You don't have access to change this quote's pricing.", errors: {} };
  }
  if (status === 409) {
    return { success: false, message: readableReason(data.message, PRICING_SAVE_FAILED), errors: {} };
  }
  return { success: false, message: PRICING_SAVE_FAILED, errors: {} };
};

const state = () => ({
  submissionsByOrgId: {},
  submissionDetailCache: {},
  messagesBySubmission: {},
  carrierMessagesByKey: {},
  bindRequestsBySubmission: {},
  loadingSubmissions: false,
  loadingSubmissionDetail: false,
  creatingSubmission: false,
});

const mutations = {
  updateField,
  setState(state, { key, value }) {
    state[key] = value;
  },
  cacheSubmissions(state, { orgId, submissions }) {
    state.submissionsByOrgId = {
      ...state.submissionsByOrgId,
      [orgId]: submissions,
    };
  },
  replaceSubmissionsCache(state, grouped) {
    state.submissionsByOrgId = grouped || {};
  },
  cacheSubmissionDetail(state, { submissionId, detail }) {
    state.submissionDetailCache = {
      ...state.submissionDetailCache,
      [submissionId]: detail,
    };
  },
  cacheMessages(state, { submissionId, messages }) {
    state.messagesBySubmission = {
      ...state.messagesBySubmission,
      [submissionId]: messages,
    };
  },
  cacheCarrierMessages(state, { submissionId, carrierOrgId, messages }) {
    const key = `${submissionId}:${carrierOrgId}`;
    state.carrierMessagesByKey = {
      ...state.carrierMessagesByKey,
      [key]: messages,
    };
  },
  setBindRequests(state, { submissionId, platformQuoteIds }) {
    state.bindRequestsBySubmission = {
      ...state.bindRequestsBySubmission,
      [submissionId]: platformQuoteIds,
    };
  },

  // Upsert a single submission row into the per-org bucket. No-op when the
  // bucket hasn't been loaded yet — realtime events from before the broker
  // opens that customer must not seed phantom buckets.
  upsertSubmissionForOrg(state, { orgId, row }) {
    if (!orgId || !row) return;
    const bucket = state.submissionsByOrgId[orgId];
    if (!Array.isArray(bucket)) return;
    const rowId = submissionRowId(row);
    if (!rowId) return;
    const idx = bucket.findIndex((existing) => submissionRowId(existing) === rowId);
    let next;
    if (idx >= 0) {
      next = bucket.slice();
      next[idx] = { ...bucket[idx], ...row };
    } else {
      next = [row, ...bucket];
    }
    Vue.set(state.submissionsByOrgId, orgId, next);
  },

  // Patch top-level (non-quote) fields of a cached submission detail so an
  // open detail view reflects status changes pushed via submissionUpdated.
  patchSubmissionDetailRowFields(state, { submissionId, row }) {
    if (!submissionId || !row) return;
    const cached = state.submissionDetailCache[submissionId];
    if (!cached || typeof cached !== "object") return;
    const cachedSub = cached.submission && typeof cached.submission === "object" ? cached.submission : null;
    const nextDetail = { ...cached };
    if (cachedSub) {
      nextDetail.submission = { ...cachedSub, ...row };
    } else {
      Object.assign(nextDetail, row);
    }
    Vue.set(state.submissionDetailCache, submissionId, nextDetail);
  },

  // Append a submission-thread message to the cached bucket, deduping by
  // entity_id so the sender's own POST + the realtime echo don't double up.
  // No-op when the bucket hasn't been loaded yet.
  appendSubmissionMessage(state, { submissionId, message }) {
    if (!submissionId || !message) return;
    const bucket = state.messagesBySubmission[submissionId];
    if (!Array.isArray(bucket)) return;
    const mid = messageRowId(message);
    if (!mid) return;
    const existingIdx = bucket.findIndex((m) => messageRowId(m) === mid);
    const next = bucket.slice();
    if (existingIdx >= 0) {
      next[existingIdx] = { ...bucket[existingIdx], ...message };
    } else {
      next.push(message);
    }
    Vue.set(state.messagesBySubmission, submissionId, next);
  },

  // Same but for the per-carrier thread bucket. The key is
  // `${submissionId}:${carrierOrgId}` to match `carrierMessages` getter.
  appendCarrierMessage(state, { submissionId, carrierOrgId, message }) {
    if (!submissionId || !carrierOrgId || !message) return;
    const key = `${submissionId}:${carrierOrgId}`;
    const bucket = state.carrierMessagesByKey[key];
    if (!Array.isArray(bucket)) return;
    const mid = messageRowId(message);
    if (!mid) return;
    const existingIdx = bucket.findIndex((m) => messageRowId(m) === mid);
    const next = bucket.slice();
    if (existingIdx >= 0) {
      next[existingIdx] = { ...bucket[existingIdx], ...message };
    } else {
      next.push(message);
    }
    Vue.set(state.carrierMessagesByKey, key, next);
  },

  // Upsert a single quote into the cached submission detail's quotes array.
  // No-op when the detail isn't cached (broker hasn't opened that submission).
  upsertQuoteInDetail(state, { submissionId, quote }) {
    if (!submissionId || !quote) return;
    const cached = state.submissionDetailCache[submissionId];
    if (!cached || typeof cached !== "object") return;
    const quotes = Array.isArray(cached.quotes) ? cached.quotes : [];
    const qid = quoteRowId(quote);
    if (!qid) return;
    const idx = quotes.findIndex((existing) => quoteRowId(existing) === qid);
    const nextQuotes = quotes.slice();
    if (idx >= 0) {
      nextQuotes[idx] = { ...quotes[idx], ...quote };
    } else {
      nextQuotes.push(quote);
    }
    Vue.set(state.submissionDetailCache, submissionId, { ...cached, quotes: nextQuotes });
  },
};

const actions = {
  async uploadSubmissionDocument(_, { orgId, formData, onUploadProgress }) {
    const { data } = await axios.post(`/brokers/customers/${orgId}/submissions/documents`, formData, {
      onUploadProgress,
      withoutLoading: true,
      transformRequest: [
        (body, headers) => {
          delete headers["Content-Type"];
          delete headers["content-type"];
          return body;
        },
      ],
    });
    return data;
  },

  async processSubmissionDocumentUpload({ dispatch }, { orgId, file, onProgress }) {
    if (!orgId) {
      throw new Error("No organization selected. Choose a client before uploading documents.");
    }
    if (!file) {
      throw new Error("No file provided");
    }

    const formData = new FormData();
    formData.append("file", file);
    const fileSize = Math.max(file.size, 0);

    const result = await dispatch("uploadSubmissionDocument", {
      orgId,
      formData,
      onUploadProgress: (e) => {
        if (!onProgress) return;
        const total = e.lengthComputable && e.total > 0 ? e.total : fileSize;
        if (!total) return;
        const pct = Math.round((e.loaded / total) * 80);
        onProgress(Math.max(0, Math.min(pct, 80)));
      },
    });

    const ref = result?.document || {};
    const handle = String(ref.handle || "").trim();
    if (!result?.success || !handle) {
      throw new Error(result?.error || "Upload failed");
    }

    if (onProgress) onProgress(100);
    return { handle };
  },

  async createSubmissionsBatch({ dispatch }, { orgId, forms, carriersMap }) {
    const carriersLookup = carriersMap && typeof carriersMap === "object" ? carriersMap : {};
    const expandedForms = forms.flatMap((f) => expandSubmissionFormToBatchForms(f));
    const submissions = expandedForms.map((f) => buildSubmissionPayload(f, carriersLookup));
    try {
      const { data } = await axios.post(`/brokers/customers/${orgId}/submissions/batch`, { submissions });
      if (data?.success) {
        // The broker's company edits were written to the company (S2CWB-333).
        // Re-read it so the next Add Submission pre-fills them without waiting
        // for the Pusher event, as the standalone BWB does. The submissions exist by now,
        // so a failed re-read must never be reported as a failed submit: the broker would
        // submit again and create duplicates.
        try {
          await dispatch("broker/refreshBrokerSidePanelCompanySetup", orgId, { root: true });
        } catch (refreshError) {
          console.warn("[brokerSubmission/createSubmissionsBatch] company refresh failed:", refreshError);
        }
      }
      return data;
    } catch (e) {
      const apiError = e?.response?.data?.error;
      if (apiError) throw new Error(apiError);
      throw e;
    }
  },

  async downloadBindPackageFile(_, { orgId, s3_key, filename }) {
    const params = new URLSearchParams({ s3_key });
    if (filename) params.set("filename", filename);
    const { data } = await axios.get(`/brokers/customers/${orgId}/files/download?${params.toString()}`, {
      responseType: "blob",
      withoutLoading: true,
    });
    return data;
  },

  async fetchSubmissionDetail({ commit }, { orgId, submissionId, refresh = false }) {
    try {
      const suffix = refresh ? `?_=${Date.now()}` : "";
      const { data } = await axios.get(`/brokers/customers/${orgId}/submissions/${submissionId}${suffix}`);
      if (!data.success) return null;
      commit("cacheSubmissionDetail", { submissionId, detail: data });
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/fetchSubmissionDetail] error:", e);
      return null;
    }
  },

  /**
   * Save one quote's pricing from the Pricing dialog (S2BWB-274), then re-read the
   * submission so every view shows the saved row. Returns `{ success, quote }`, or
   * `{ success: false, message, errors }` with the API's field messages for a refused
   * field. No global error toast: the dialog says what went wrong.
   */
  async saveQuotePricing({ commit, dispatch }, { orgId, submissionId, quoteId, pricing }) {
    if (!orgId || !submissionId || !quoteId) {
      return { success: false, message: PRICING_SAVE_FAILED, errors: {} };
    }
    try {
      const { data } = await axios.put(
        `/brokers/customers/${orgId}/submissions/${submissionId}/quotes/${quoteId}/pricing`,
        pricing,
        { withoutErrorHandler: true }
      );
      if (!data?.success) return { success: false, message: PRICING_SAVE_FAILED, errors: {} };
      if (data.quote) commit("upsertQuoteInDetail", { submissionId, quote: data.quote });
      await dispatch("fetchSubmissionDetail", { orgId, submissionId, refresh: true });
      return { success: true, quote: data.quote || null };
    } catch (e) {
      // The quote changed or went away since the dialog opened: re-read it so the
      // page and a reopened dialog show what is stored now.
      const status = e?.response?.status;
      if (status === 404 || (status === 400 && e?.response?.data?.error_code === "stale_pricing")) {
        await dispatch("fetchSubmissionDetail", { orgId, submissionId, refresh: true });
      }
      return pricingFailure(e);
    }
  },

  async fetchSubmissionMessages({ commit }, { orgId, submissionId }) {
    try {
      const { data } = await axios.get(`/brokers/customers/${orgId}/submissions/${submissionId}/messages`);
      // Cache the full list so the modal can render off Vuex and realtime
      // appendSubmissionMessage commits land in the same bucket the UI reads.
      if (data?.success) {
        commit("cacheMessages", { submissionId, messages: data.messages || [] });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/fetchSubmissionMessages] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Unable to load messages."),
        messages: [],
      };
    }
  },

  async postSubmissionMessage({ commit }, { orgId, submissionId, text }) {
    try {
      const { data } = await axios.post(`/brokers/customers/${orgId}/submissions/${submissionId}/messages`, { text });
      // Append the saved message to the cache from the HTTP response so the
      // sender's own UI shows it immediately. The realtime echo will dedupe
      // by entity_id.
      if (data?.success && data?.message) {
        commit("appendSubmissionMessage", { submissionId, message: data.message });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/postSubmissionMessage] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Unable to send message."),
      };
    }
  },

  async fetchSubmissionCarrierMessages({ commit }, { orgId, submissionId, carrierOrgId }) {
    try {
      const { data } = await axios.get(
        `/brokers/customers/${orgId}/submissions/${submissionId}/carriers/${carrierOrgId}/messages`
      );
      if (data?.success) {
        commit("cacheCarrierMessages", {
          submissionId,
          carrierOrgId,
          messages: data.messages || [],
        });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/fetchSubmissionCarrierMessages] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Unable to load messages."),
        messages: [],
      };
    }
  },

  async postSubmissionCarrierMessage({ commit }, { orgId, submissionId, carrierOrgId, text }) {
    try {
      const { data } = await axios.post(
        `/brokers/customers/${orgId}/submissions/${submissionId}/carriers/${carrierOrgId}/messages`,
        { text }
      );
      if (data?.success && data?.message) {
        commit("appendCarrierMessage", { submissionId, carrierOrgId, message: data.message });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/postSubmissionCarrierMessage] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Unable to send message."),
      };
    }
  },

  // Realtime: a broker, carrier, or system event message was created on a
  // submission this broker can see. Append to whichever buckets are loaded
  // (submission-thread always, per-carrier only when present + cached).
  pusherSubmissionMessageCreated({ commit }, message) {
    if (!message) return;
    const submissionId = message.submission_id || message.submissionId;
    if (!submissionId) return;
    commit("appendSubmissionMessage", { submissionId, message });
    const carrierOrgId = carrierKeyOf(message);
    if (carrierOrgId) {
      commit("appendCarrierMessage", { submissionId, carrierOrgId, message });
    }
  },

  async fetchCustomerSubmissions({ commit }, { orgId }) {
    try {
      const { data } = await axios.get(`/brokers/customers/${orgId}/submissions`);
      if (!data.success) return [];
      const submissions = data.submissions || [];
      commit("cacheSubmissions", { orgId, submissions });
      return submissions;
    } catch (e) {
      console.warn("[brokerSubmission/fetchCustomerSubmissions] error:", e);
      return [];
    }
  },

  async fetchBrokerSubmissions({ commit }, { filters = {} } = {}) {
    commit("setState", { key: "loadingSubmissions", value: true });
    try {
      const params = {};
      const searchTerm = String(filters?.search_term || "").trim();
      if (searchTerm) params.search_term = searchTerm;
      if (filters?.company_status) params.company_status = filters.company_status;
      if (filters?.customer_books) params.customer_books = filters.customer_books;

      const { data } = await axios.get("/brokers/customers/submissions", { params });
      if (!data?.success) return;
      commit("replaceSubmissionsCache", data.submissionsByOrgId || {});
    } catch (e) {
      console.warn("[brokerSubmission/fetchBrokerSubmissions] error:", e);
    } finally {
      commit("setState", { key: "loadingSubmissions", value: false });
    }
  },

  pusherSubmissionCreated({ commit, state }, row) {
    if (!row) return;
    const orgId = row.organizationId || row.organization_id;
    if (!orgId) return;
    if (!Array.isArray(state.submissionsByOrgId[orgId])) {
      commit("cacheSubmissions", { orgId, submissions: [row] });
      return;
    }
    commit("upsertSubmissionForOrg", { orgId, row });
  },

  pusherSubmissionUpdated({ commit, state }, row) {
    if (!row) return;
    const orgId = row.organizationId || row.organization_id;
    if (orgId) {
      if (Array.isArray(state.submissionsByOrgId[orgId])) {
        commit("upsertSubmissionForOrg", { orgId, row });
      } else {
        commit("cacheSubmissions", { orgId, submissions: [row] });
      }
    }
    const submissionId = submissionRowId(row);
    if (submissionId && state.submissionDetailCache[submissionId]) {
      commit("patchSubmissionDetailRowFields", { submissionId, row });
    }
  },

  pusherQuoteCreated({ commit }, quote) {
    if (!quote) return;
    const submissionId = quote.submissionId || quote.submission_id;
    if (!submissionId) return;
    commit("upsertQuoteInDetail", { submissionId, quote });
  },

  pusherQuoteUpdated({ commit, state, rootState, dispatch }, quote) {
    if (!quote) return;
    const submissionId = quote.submissionId || quote.submission_id;
    if (!submissionId) return;
    commit("upsertQuoteInDetail", { submissionId, quote });
    // Soft-refresh comparison when cached so analysis updates land without a full reload.
    const comparisonState = (rootState && rootState.coverageComparison) || {};
    const comparison = comparisonState.comparisonCache && comparisonState.comparisonCache[submissionId];
    if (!comparison || typeof dispatch !== "function") return;
    const detail = state.submissionDetailCache && state.submissionDetailCache[submissionId];
    const orgId =
      quote.organizationId ||
      quote.organization_id ||
      comparison.organizationId ||
      comparison.organization_id ||
      (detail && detail.submission && (detail.submission.organizationId || detail.submission.organization_id)) ||
      null;
    if (!orgId) return;
    dispatch(
      "coverageComparison/fetchComparison",
      { orgId, submissionId, refresh: true, silent: true },
      { root: true }
    );
    if (comparisonState.advisoryCache && comparisonState.advisoryCache[submissionId]) {
      dispatch(
        "coverageComparison/fetchAdvisory",
        { orgId, submissionId, refresh: true, silent: true },
        { root: true }
      );
    }
  },

  async bindQuote({ commit }, { orgId, platformQuoteId, documents }) {
    try {
      const body = documents ? { documents } : {};
      const { data } = await axios.post(`/brokers/customers/${orgId}/quotes/${platformQuoteId}/bind`, body);
      // Patch the cache from the HTTP response so the UI reflects the new
      // state immediately, without waiting on the realtime quoteUpdated event.
      if (data?.success && data?.quote && data?.submissionId) {
        commit("upsertQuoteInDetail", {
          submissionId: data.submissionId,
          quote: { ...data.quote, platformQuoteId: data.platformQuoteId || platformQuoteId },
        });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/bindQuote] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Bind request failed."),
      };
    }
  },

  async withdrawBindQuote({ commit }, { orgId, platformQuoteId }) {
    try {
      const { data } = await axios.delete(`/brokers/customers/${orgId}/quotes/${platformQuoteId}/bind`);
      if (data?.success && data?.quote && data?.submissionId) {
        commit("upsertQuoteInDetail", {
          submissionId: data.submissionId,
          quote: { ...data.quote, platformQuoteId: data.platformQuoteId || platformQuoteId },
        });
      }
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/withdrawBindQuote] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Unable to withdraw bind request."),
      };
    }
  },

  async withdrawSubmission(_, { orgId, submissionId, carrierIds }) {
    try {
      const { data } = await axios.post(`/brokers/customers/${orgId}/submissions/${submissionId}/withdraw`, {
        carrierIds,
      });
      return data;
    } catch (e) {
      console.warn("[brokerSubmission/withdrawSubmission] error:", e);
      return {
        success: false,
        message: readableReason(e?.response?.data?.message, "Withdrawal failed. Please try again."),
      };
    }
  },

  async resubmitSubmission({ commit }, { orgId, submissionId }) {
    // Re-sends a Failed submission to UWB (S2CWB-333). The backend reuses each
    // carrier leg's id, so a resubmit never duplicates a leg UWB already has.
    try {
      const { data } = await axios.post(`/brokers/customers/${orgId}/submissions/${submissionId}/resubmit`, {});
      if (data && data.success) {
        commit("cacheSubmissionDetail", { submissionId, detail: null });
      }
      return data;
    } catch (e) {
      // The axios interceptor has already shown the API's message.
      return {
        success: false,
        apiError: Boolean(e?.response),
        message: readableReason(
          e?.response?.data?.message || e?.response?.data?.error,
          "Resubmit failed. Please try again."
        ),
      };
    }
  },

  async fetchBffCarriers({ commit }) {
    try {
      const { data } = await axios.get("/brokers/carriers");
      const carriers = data.carriers || data || [];
      commit("setState", { key: "bffCarriers", value: carriers });
      return carriers;
    } catch (error) {
      console.warn("[brokerSubmission] /brokers/carriers unavailable", error);
      return [];
    }
  },
};

const getters = {
  getField,
  submissionsForOrg: (state) => (orgId) => state.submissionsByOrgId[orgId] || [],
  submissionDetail: (state) => (submissionId) => state.submissionDetailCache[submissionId] || null,
  messagesForSubmission: (state) => (submissionId) => state.messagesBySubmission[submissionId] || [],
  carrierMessages: (state) => (submissionId, carrierOrgId) =>
    state.carrierMessagesByKey[`${submissionId}:${carrierOrgId}`] || [],
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
